/*
 * SPDX-FileCopyrightText: 2026 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { getNoteMetadata } from '../../../api/notes'
import { useApplicationState } from '../../../hooks/common/use-application-state'
import { setNotePermissionsFromServer } from '../../../redux/note-details/methods'
import { setNoteTreeEntryOwner } from '../../../redux/note-tree/methods'
import { AsyncLoadingBoundary } from '../async-loading-boundary/async-loading-boundary'
import type { NoteMetadataInterface, NotePermissionsInterface } from '@hedgedoc/commons'
import { userCanEdit, userIsOwner } from '@hedgedoc/commons'
import type { PropsWithChildren } from 'react'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useAsync } from 'react-use'

export type NoteInfo = Pick<NoteMetadataInterface, 'createdAt' | 'updatedAt' | 'lastUpdatedBy' | 'editedBy'>

export interface NoteSettingsContextValue {
  /** The alias of the note whose settings are shown */
  noteAlias: string
  /** Whether the note is the one that is currently opened in the editor */
  isOpenNote: boolean
  /** Information about the creation and the changes of the note */
  info: NoteInfo
  /** The current permissions of the note */
  permissions: NotePermissionsInterface
  /** Whether the current user is the owner of the note */
  isOwner: boolean
  /** Whether the current user may edit the note */
  mayEdit: boolean
  /** Must be called with the permissions the backend returned after a change */
  onPermissionsChange: (permissions: NotePermissionsInterface) => void
}

const NoteSettingsContext = createContext<NoteSettingsContextValue | undefined>(undefined)

/**
 * Returns the info and permissions of the note that is provided by the surrounding {@link NoteSettingsProvider}.
 *
 * @throws Error if there is no surrounding {@link NoteSettingsProvider}
 */
export const useNoteSettingsContext = (): NoteSettingsContextValue => {
  const context = useContext(NoteSettingsContext)
  if (context === undefined) {
    throw new Error('No note settings context found. Did you forget to use the NoteSettingsProvider?')
  }
  return context
}

interface NoteSettingsProviderProps extends PropsWithChildren {
  /** The alias of the note whose settings should be shown and edited */
  noteAlias: string
}

/**
 * Provides the info and permissions of a note to the page settings.
 * The data of the currently opened note is taken from and written to the global state,
 * the data of any other note is fetched from the backend.
 *
 * @param noteAlias The alias of the note whose settings should be shown and edited
 * @param children The components that use the note settings
 */
export const NoteSettingsProvider: React.FC<NoteSettingsProviderProps> = ({ noteAlias, children }) => {
  const openNoteAlias = useApplicationState((state) => state.noteDetails?.primaryAlias)
  const openNotePermissions = useApplicationState((state) => state.noteDetails?.permissions)
  const openNoteCreatedAt = useApplicationState((state) => state.noteDetails?.createdAt)
  const openNoteUpdatedAt = useApplicationState((state) => state.noteDetails?.updatedAt)
  const openNoteLastUpdatedBy = useApplicationState((state) => state.noteDetails?.lastUpdatedBy)
  const openNoteEditedBy = useApplicationState((state) => state.noteDetails?.editedBy)
  const me = useApplicationState((state) => state.user?.username)
  const isOpenNote = noteAlias === openNoteAlias

  const {
    loading,
    error,
    value: fetchedMetadata
  } = useAsync(async () => (isOpenNote ? undefined : await getNoteMetadata(noteAlias)), [isOpenNote, noteAlias])
  const [fetchedPermissions, setFetchedPermissions] = useState<NotePermissionsInterface>()
  useEffect(() => {
    setFetchedPermissions(fetchedMetadata?.permissions)
  }, [fetchedMetadata])

  const permissions = isOpenNote ? openNotePermissions : fetchedPermissions

  const info = useMemo<NoteInfo | undefined>(() => {
    if (!isOpenNote) {
      return fetchedMetadata
    }
    if (openNoteCreatedAt === undefined || openNoteUpdatedAt === undefined || openNoteEditedBy === undefined) {
      return undefined
    }
    return {
      createdAt: openNoteCreatedAt,
      updatedAt: openNoteUpdatedAt,
      lastUpdatedBy: openNoteLastUpdatedBy ?? null,
      editedBy: openNoteEditedBy
    }
  }, [fetchedMetadata, isOpenNote, openNoteCreatedAt, openNoteEditedBy, openNoteLastUpdatedBy, openNoteUpdatedAt])

  const onPermissionsChange = useCallback(
    (newPermissions: NotePermissionsInterface) => {
      if (isOpenNote) {
        setNotePermissionsFromServer(newPermissions)
      } else {
        setFetchedPermissions(newPermissions)
      }
      setNoteTreeEntryOwner({ primaryAlias: noteAlias, owner: newPermissions.owner })
    },
    [isOpenNote, noteAlias]
  )

  const contextValue = useMemo<NoteSettingsContextValue | undefined>(
    () =>
      info === undefined || permissions === undefined
        ? undefined
        : {
            noteAlias,
            isOpenNote,
            info,
            permissions,
            isOwner: userIsOwner(permissions, me),
            mayEdit: userCanEdit(permissions, me),
            onPermissionsChange
          },
    [info, isOpenNote, me, noteAlias, onPermissionsChange, permissions]
  )

  return (
    <AsyncLoadingBoundary
      loading={loading || (!error && contextValue === undefined)}
      error={error}
      componentName={'NoteSettingsProvider'}>
      <NoteSettingsContext.Provider value={contextValue}>{children}</NoteSettingsContext.Provider>
    </AsyncLoadingBoundary>
  )
}
